import type { UseQueryOptions } from '@tanstack/react-query';
import { extractList, sdk, type OrgFundraiserOut } from '@givmo/api-client';
import {
  fundraisersGetOwnFundraiserQueryKey,
  orgFundraisersGetOrgFundraiserQueryKey,
  orgFundraisersListOrgFundraisersQueryKey,
} from '@givmo/api-client/query';
import type { FundraiserOut } from '@givmo/api-client';

export const PORTFOLIO_PAGE_SIZE = 25;
export type PortfolioState = 'all' | 'active' | 'draft' | 'closed' | 'deactivated';

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/** An inner read never treats a different organization as the active one. */
export function requireOrgFundraiser(value: unknown, orgRef: string): OrgFundraiserOut {
  const row = isRecord(value) && isRecord(value.data) ? value.data : value;
  if (
    !isRecord(row) ||
    row.org_ref !== orgRef ||
    typeof row.id !== 'string' ||
    typeof row.name !== 'string' ||
    typeof row.state !== 'string' ||
    !isRecord(row.stats) ||
    typeof row.stats.total_raised !== 'string'
  ) {
    throw new Error('The fundraiser response did not match this organization.');
  }
  return row as unknown as OrgFundraiserOut;
}

export function requireOrgFundraisers(body: unknown, orgRef: string): OrgFundraiserOut[] {
  if (!Array.isArray(body) && !(isRecord(body) && Array.isArray(body.data))) {
    throw new Error('The fundraiser list could not be read.');
  }
  return extractList<OrgFundraiserOut>(body).map((row) => requireOrgFundraiser(row, orgRef));
}

export function portfolioOptions(
  orgRef: string,
  state: PortfolioState = 'all',
  offset = 0,
): UseQueryOptions<OrgFundraiserOut[], Error> {
  // The extra row is a sentinel because the shared API client unwraps list
  // envelopes. State filtering happens on the server before this pagination.
  const options = {
    path: { org_ref: orgRef },
    query: {
      'page[limit]': PORTFOLIO_PAGE_SIZE + 1,
      'page[offset]': offset,
      ...(state === 'all' ? {} : { state }),
    },
  };
  return {
    queryKey: orgFundraisersListOrgFundraisersQueryKey(options),
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      const { data } = await sdk.orgFundraisersListOrgFundraisers({
        ...options,
        signal,
        throwOnError: true,
      });
      return requireOrgFundraisers(data, orgRef);
    },
  };
}

/** Called only after the organization detail has proved scope. Never use the public detail for drafts. */
export function privateFundraiserOptions(
  fundraiserId: string,
): UseQueryOptions<FundraiserOut, Error> {
  const options = { path: { fundraiser_id: fundraiserId } };
  return {
    queryKey: fundraisersGetOwnFundraiserQueryKey(options),
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      const { data } = await sdk.fundraisersGetOwnFundraiser({
        ...options,
        signal,
        throwOnError: true,
      });
      const body: unknown = data;
      const row: unknown = isRecord(body) && isRecord(body.data) ? body.data : body;
      if (
        !isRecord(row) ||
        row.id !== fundraiserId ||
        !Array.isArray(row.charities) ||
        !Array.isArray(row.etfs)
      ) {
        throw new Error('The fundraiser details could not be read.');
      }
      return row as unknown as FundraiserOut;
    },
  };
}

export function fundraiserOptions(
  orgRef: string,
  fundraiserId: string,
): UseQueryOptions<OrgFundraiserOut, Error> {
  const options = { path: { org_ref: orgRef, fundraiser_id: fundraiserId } };
  return {
    queryKey: orgFundraisersGetOrgFundraiserQueryKey(options),
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      const { data } = await sdk.orgFundraisersGetOrgFundraiser({
        ...options,
        signal,
        throwOnError: true,
      });
      const row = requireOrgFundraiser(data, orgRef);
      if (row.id !== fundraiserId) throw new Error('The fundraiser response had a different id.');
      return row;
    },
  };
}
